import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Oblique Design System — SharePanel
 * ═══════════════════════════════════════════════════════════════════════════
 */

const PANEL_VARIANTS = {
    initial: { opacity: 0, y: 6, scale: 0.97 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit:    { opacity: 0, y: 4, scale: 0.97 },
};
const PANEL_TRANSITION = { duration: 0.15, ease: [0.16, 1, 0.3, 1] };

export function SharePanel({ 
    titleString, 
    onCopyLink, 
    onClose,
    copyMode = 'link-only',
    onCopyTable,
    onCopyText,
    onCopySlack,
    onExport,
    onSendEmail
}) {
    const [shareTab, setShareTab] = useState('share');
    const [showCopyDropdown, setShowCopyDropdown] = useState(false);
    const [selectedFormat, setSelectedFormat] = useState('table'); // 'table' | 'slack' | 'link' | 'tsv' | 'csv'
    const copyDropdownRef = useRef(null);

    useEffect(() => {
        if (!showCopyDropdown) return;
        const handleOutside = (e) => {
            if (copyDropdownRef.current && !copyDropdownRef.current.contains(e.target)) {
                setShowCopyDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [showCopyDropdown]);

    const handleDefaultCopy = () => {
        if (selectedFormat === 'link') {
            if (onCopyLink) onCopyLink();
            else navigator.clipboard.writeText(window.location.href);
        } else if (selectedFormat === 'slack' && onCopySlack) {
            onCopySlack();
        } else if (selectedFormat === 'tsv' && onCopyText) {
            onCopyText();
        } else if (selectedFormat === 'csv' && onExport) {
            onExport();
        } else if (onCopyTable) {
            onCopyTable();
        } else if (onCopyLink) {
            onCopyLink();
        } else {
            navigator.clipboard.writeText(window.location.href);
        }
        if (onClose) onClose();
    };

    return (
        <motion.div
            initial="initial" animate="animate" exit="exit"
            variants={PANEL_VARIANTS} transition={PANEL_TRANSITION}
            className="absolute top-[calc(100%+4px)] right-0 w-[380px] bg-white dark:bg-[#161b2e] border border-[var(--border)] rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.22)] z-[3000] flex flex-col font-['Lexend_Deca',sans-serif]"
        >
            <div className="flex border-b border-[var(--border)] px-2 bg-slate-50/60 dark:bg-slate-900/60 rounded-t-xl">
                <button 
                    onClick={() => setShareTab('share')}
                    className={`py-3 px-4 border-none bg-transparent cursor-pointer text-[14px] font-medium border-b-2 mb-[-1px] transition-all duration-200 ${shareTab === 'share' ? 'text-[var(--text-primary)] border-[var(--text-primary)]' : 'text-[var(--text-secondary)] border-transparent'}`}
                >
                    Share
                </button>
                <button 
                    onClick={() => setShareTab('publish')}
                    className={`py-3 px-4 border-none bg-transparent cursor-pointer text-[14px] font-medium border-b-2 mb-[-1px] transition-all duration-200 ${shareTab === 'publish' ? 'text-[var(--text-primary)] border-[var(--text-primary)]' : 'text-[var(--text-secondary)] border-transparent'}`}
                >
                    Publish
                </button>
            </div>

            {shareTab === 'share' && (
                <div className="p-4 flex flex-col gap-4">
                    <div className="flex gap-2">
                        <input 
                            type="text" 
                            placeholder="Email or group..." 
                            className="flex-1 py-2 px-3 rounded-md border border-[var(--border)] text-[14px] bg-white dark:bg-[#1a2035] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)]"
                        />
                        <button className="py-2 px-4 rounded-md bg-[var(--accent)] text-white border-none text-[14px] font-medium cursor-pointer hover:opacity-90">
                            Invite
                        </button>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[var(--accent)] text-white flex items-center justify-center text-[14px] font-medium">
                                S
                            </div>
                            <div>
                                <div className="text-[14px] font-medium text-[var(--text-primary)]">Sanjay Dev <span className="text-[var(--text-tertiary)] font-normal">(You)</span></div>
                                <div className="text-[12px] text-[var(--text-secondary)]">isanjaydev@gmail.com</div>
                            </div>
                        </div>
                        <div className="text-[13px] text-[var(--text-secondary)] cursor-pointer flex items-center hover:text-[var(--text-primary)]">
                            Full access <ObliqueIcon name="expand_more" size={14} className="ml-0.5" />
                        </div>
                    </div>
                    
                    <div className="py-4 border-t border-b border-[var(--border)]">
                        <div className="text-[12px] font-medium text-[var(--text-secondary)] mb-3">General access</div>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-md bg-[var(--bg-muted)] flex items-center justify-center">
                                <ObliqueIcon name="lock" size={16} color="var(--text-secondary)" />
                            </div>
                            <div>
                                <div className="text-[14px] font-medium text-[var(--text-primary)] cursor-pointer hover:opacity-80">Only people invited <ObliqueIcon name="expand_more" size={14} className="align-middle text-[var(--text-tertiary)]" /></div>
                            </div>
                        </div>
                    </div>

                    <div className="border border-[var(--border)] rounded-lg p-3">
                        <div className="flex justify-between mb-2">
                            <div className="text-[12px] font-medium text-[var(--text-secondary)]">Page-level access</div>
                            <ObliqueIcon name="help_outline" size={14} color="var(--text-tertiary)" className="cursor-help" />
                        </div>
                        <div className="flex items-center gap-3 cursor-pointer group">
                            <div className="w-8 h-8 rounded-md bg-[#e0f2fe] dark:bg-[var(--accent-subtle)] flex items-center justify-center">
                                <ObliqueIcon name="add" size={16} color="var(--accent)" />
                            </div>
                            <div>
                                <div className="text-[14px] font-medium text-[var(--text-primary)] group-hover:text-[var(--accent)] transition-colors">Add a new rule</div>
                                <div className="text-[12px] text-[var(--text-tertiary)]">Set conditional access rules</div>
                            </div>
                        </div>
                    </div>

                    {/* Send Email - clean inline row */}
                    {onSendEmail && (
                        <button
                            onClick={() => { if (onClose) onClose(); onSendEmail(); }}
                            className="w-full flex items-center gap-3 py-2.5 px-3 rounded-lg border border-[var(--border)] bg-transparent hover:bg-[var(--bg-subtle)] cursor-pointer transition-all duration-150 text-left group"
                        >
                            <div className="w-8 h-8 rounded-md bg-[var(--bg-muted)] flex items-center justify-center text-[var(--text-secondary)] group-hover:text-[var(--accent)] transition-colors">
                                <ObliqueIcon name="mail" size={16} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-medium text-[var(--text-primary)] group-hover:text-[var(--accent)] transition-colors">Send Email</div>
                                <div className="text-[11px] text-[var(--text-tertiary)]">Send report summary via email</div>
                            </div>
                            <ObliqueIcon name="chevron_right" size={14} className="text-[var(--text-tertiary)] group-hover:text-[var(--accent)] transition-colors" />
                        </button>
                    )}

                    <div className="flex justify-between items-center mt-1">
                        <div className="text-[13px] text-[var(--text-secondary)] flex items-center gap-1.5 cursor-pointer hover:text-[var(--text-primary)]">
                            <ObliqueIcon name="help_outline" size={14} /> Learn about sharing
                        </div>
                        {copyMode === 'dropdown' || onCopyTable ? (
                            <div ref={copyDropdownRef} className="relative flex items-center shadow-sm">
                                <button
                                    onClick={handleDefaultCopy}
                                    className="h-[28px] py-0 px-3 bg-[#2383e2] hover:bg-[#1a6ec0] text-white border-none rounded-l text-[13px] font-medium cursor-pointer flex items-center gap-1.5 justify-center transition-colors select-none"
                                    title="Copy"
                                >
                                    <ObliqueIcon name="content_copy" size={13} className="text-white" />
                                    <span>Copy</span>
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowCopyDropdown(prev => !prev); }}
                                    className="h-[28px] w-6 bg-[#2383e2] hover:bg-[#1a6ec0] text-white border-none border-l border-white/30 rounded-r flex items-center justify-center cursor-pointer transition-colors select-none"
                                    title="Choose format to copy"
                                >
                                    <ObliqueIcon name="expand_more" size={15} weight={500} className={`transition-transform duration-200 ${showCopyDropdown ? 'rotate-180' : 'rotate-0'}`} />
                                </button>

                                <AnimatePresence>
                                    {showCopyDropdown && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 4, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: 4, scale: 0.95 }}
                                            transition={{ duration: 0.12 }}
                                            className="absolute bottom-[calc(100%+6px)] right-0 w-[240px] bg-white dark:bg-[#161b2e] border border-[var(--border)] rounded-xl shadow-[0_12px_32px_rgba(0,0,0,0.2)] p-1.5 z-[3100]"
                                        >
                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2.5 py-1">
                                                Copy Formats
                                            </div>
                                            <button
                                                onClick={() => { 
                                                    setSelectedFormat('link');
                                                    setShowCopyDropdown(false); 
                                                    if (onCopyLink) onCopyLink();
                                                    else navigator.clipboard.writeText(window.location.href);
                                                    if (onClose) onClose(); 
                                                }}
                                                className="w-full flex items-center justify-between px-2.5 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-[var(--bg-muted)] rounded-lg cursor-pointer transition-colors text-left"
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    <ObliqueIcon name="link" size={14} className="text-blue-500" />
                                                    <div>
                                                        <div>Link</div>
                                                        <div className="text-[10px] text-slate-400 font-normal">Page URL for sharing</div>
                                                    </div>
                                                </div>
                                                {selectedFormat === 'link' && <ObliqueIcon name="check" size={14} className="text-[var(--accent)]" />}
                                            </button>
                                            {onCopyTable && (
                                                <button
                                                    onClick={() => { 
                                                        setSelectedFormat('table');
                                                        setShowCopyDropdown(false); 
                                                        onCopyTable(); 
                                                        if (onClose) onClose(); 
                                                    }}
                                                    className="w-full flex items-center justify-between px-2.5 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-[var(--bg-muted)] rounded-lg cursor-pointer transition-colors text-left"
                                                >
                                                    <div className="flex items-center gap-2.5">
                                                        <ObliqueIcon name="table_chart" size={14} className="text-indigo-500" />
                                                        <div>
                                                            <div>Rich Table</div>
                                                            <div className="text-[10px] text-slate-400 font-normal">For Gmail, Docs, Sheets</div>
                                                        </div>
                                                    </div>
                                                    {selectedFormat === 'table' && <ObliqueIcon name="check" size={14} className="text-[var(--accent)]" />}
                                                </button>
                                            )}
                                            {onCopySlack && (
                                                <button
                                                    onClick={() => { 
                                                        setSelectedFormat('slack');
                                                        setShowCopyDropdown(false); 
                                                        onCopySlack(); 
                                                        if (onClose) onClose(); 
                                                    }}
                                                    className="w-full flex items-center justify-between px-2.5 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-[var(--bg-muted)] rounded-lg cursor-pointer transition-colors text-left"
                                                >
                                                    <div className="flex items-center gap-2.5">
                                                        <ObliqueIcon name="forum" size={14} className="text-emerald-500" />
                                                        <div>
                                                            <div>Slack / Teams Summary</div>
                                                            <div className="text-[10px] text-slate-400 font-normal">Formatted highlights & metrics</div>
                                                        </div>
                                                    </div>
                                                    {selectedFormat === 'slack' && <ObliqueIcon name="check" size={14} className="text-[var(--accent)]" />}
                                                </button>
                                            )}
                                            {onCopyText && (
                                                <button
                                                    onClick={() => { 
                                                        setSelectedFormat('tsv');
                                                        setShowCopyDropdown(false); 
                                                        onCopyText(); 
                                                        if (onClose) onClose(); 
                                                    }}
                                                    className="w-full flex items-center justify-between px-2.5 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-[var(--bg-muted)] rounded-lg cursor-pointer transition-colors text-left"
                                                >
                                                    <div className="flex items-center gap-2.5">
                                                        <ObliqueIcon name="description" size={14} className="text-amber-500" />
                                                        <div>
                                                            <div>Plain Text (TSV)</div>
                                                            <div className="text-[10px] text-slate-400 font-normal">Raw tab-separated table</div>
                                                        </div>
                                                    </div>
                                                    {selectedFormat === 'tsv' && <ObliqueIcon name="check" size={14} className="text-[var(--accent)]" />}
                                                </button>
                                            )}
                                            {onExport && (
                                                <button
                                                    onClick={() => { 
                                                        setSelectedFormat('csv');
                                                        setShowCopyDropdown(false); 
                                                        onExport(); 
                                                        if (onClose) onClose(); 
                                                    }}
                                                    className="w-full flex items-center justify-between px-2.5 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-[var(--bg-muted)] rounded-lg cursor-pointer transition-colors text-left"
                                                >
                                                    <div className="flex items-center gap-2.5">
                                                        <ObliqueIcon name="download" size={14} className="text-blue-500" />
                                                        <div>
                                                            <div>Export CSV</div>
                                                            <div className="text-[10px] text-slate-400 font-normal">Download data as CSV file</div>
                                                        </div>
                                                    </div>
                                                    {selectedFormat === 'csv' && <ObliqueIcon name="check" size={14} className="text-[var(--accent)]" />}
                                                </button>
                                            )}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ) : (
                            <button 
                                onClick={onCopyLink || (() => { navigator.clipboard.writeText(window.location.href); if (onClose) onClose(); })}
                                className="flex items-center gap-2 py-1.5 px-3 rounded-md border border-[var(--border)] bg-transparent cursor-pointer text-[13px] font-medium text-[var(--text-primary)] hover:bg-[var(--bg-subtle)]"
                            >
                                <ObliqueIcon name="link" size={14} /> Copy link
                            </button>
                        )}
                    </div>
                </div>
            )}

            {shareTab === 'publish' && (
                <div className="p-6 flex flex-col items-center text-center">
                    <div className="text-[16px] font-medium text-[var(--text-primary)] mb-1">Publish to web</div>
                    <div className="text-[13px] text-[var(--text-secondary)] mb-6 flex items-center gap-1">
                        Create a website with Oblique <ObliqueIcon name="help_outline" size={14} className="cursor-help" />
                    </div>

                    <div className="w-full h-[120px] border border-[var(--border)] rounded-lg bg-[var(--bg-muted)] flex items-center justify-center mb-6">
                        <div className="text-[20px] font-medium text-[var(--text-primary)]">{titleString || 'Financial DB'}</div>
                    </div>

                    <button className="w-full py-2.5 px-4 rounded-md bg-[var(--accent)] text-white border-none text-[14px] font-medium cursor-pointer mb-4 hover:opacity-90">
                        Publish
                    </button>

                    <div className="text-[12px] text-[var(--text-tertiary)] flex items-start gap-1.5 text-left">
                        <ObliqueIcon name="info" size={14} className="shrink-0 mt-[2px]" />
                        When published to web, anyone with the link can view this page's content and see contributor names.
                    </div>
                </div>
            )}
        </motion.div>
    );
}
